import { Button, Checkbox, Input, Select } from './ui';
import { formFieldSchema } from '@kioskforge/presets';

/**
 * A5b.7 — visitor-form field builder. Edits a JSON array of form fields in a
 * single-line config value (`values[fieldKey]`), the shape `compilePreset`
 * expects: `[{id,label,type,required,options?}]`.
 *
 * Empty string means "no field list", which is valid: the checkin preset's
 * `fields` is optional and falls back to the skeleton's single field. So this
 * control never writes an invalid placeholder.
 *
 * The row shape is validated against the preset's own `formFieldSchema` (shared
 * from `@kioskforge/presets`) so the builder and the preset cannot drift.
 */

const TYPES = ['text', 'email', 'phone', 'select', 'host'];

/** Parse a config value into rows; anything unparseable is treated as empty. */
export function parseFieldRows(value) {
  if (!value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => formFieldSchema.safeParse(item))
      .filter((result) => result.success)
      .map((result) => result.data);
  } catch {
    return [];
  }
}

function serialise(rows) {
  return rows.length === 0 ? '' : JSON.stringify(rows);
}

/** Default a new row from its label, so editing never produces an invalid id. */
function idFromLabel(label) {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return slug || 'field';
}

export function FieldListEditor({ id, value, onChange }) {
  const rows = parseFieldRows(value);

  function commit(next) {
    onChange(serialise(next));
  }

  function patch(index, changes) {
    commit(rows.map((row, at) => (at === index ? { ...row, ...changes } : row)));
  }

  function addRow() {
    const label = `Field ${rows.length + 1}`;
    commit([...rows, { id: idFromLabel(label), label, type: 'text', required: false }]);
  }

  function removeRow(index) {
    commit(rows.filter((_, at) => at !== index));
  }

  function move(index, delta) {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const [row] = next.splice(index, 1);
    if (row) next.splice(target, 0, row);
    commit(next);
  }

  return (
    <div className="field-list" id={id}>
      {rows.length === 0 ? (
        <p className="detail-note">No fields yet — the preset's default single field is used.</p>
      ) : (
        <ol className="field-rows">
          {rows.map((row, index) => (
            <li key={`${row.id}-${index}`} className="field-row">
              <label className="index-label" htmlFor={`${id}-label-${index}`}>
                LABEL
              </label>
              <Input
                id={`${id}-label-${index}`}
                value={row.label}
                onChange={(event) => patch(index, { label: event.target.value })}
              />
              <label className="index-label" htmlFor={`${id}-id-${index}`}>
                ID
              </label>
              <Input
                id={`${id}-id-${index}`}
                value={row.id}
                onChange={(event) => patch(index, { id: idFromLabel(event.target.value) })}
              />
              <label className="index-label" htmlFor={`${id}-type-${index}`}>
                TYPE
              </label>
              <Select
                id={`${id}-type-${index}`}
                value={row.type}
                onChange={(event) => patch(index, { type: event.target.value })}
              >
                {TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
              <Checkbox
                label="Required"
                checked={row.required}
                onChange={(event) => patch(index, { required: event.target.checked })}
              />
              <div className="row-actions">
                <Button variant="quiet" type="button" onClick={() => move(index, -1)} disabled={index === 0}>
                  Up
                </Button>
                <Button variant="quiet" type="button" onClick={() => move(index, 1)} disabled={index === rows.length - 1}>
                  Down
                </Button>
                <Button variant="danger" type="button" onClick={() => removeRow(index)}>
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}
      <Button variant="quiet" type="button" onClick={addRow}>
        Add field
      </Button>
    </div>
  );
}
